import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MessageCircle, 
  X, 
  Sparkles, 
  Wand2, 
  HelpCircle,
  BookOpen,
  Loader2,
  Copy,
  Check,
  ChevronDown,
  ChevronRight,
  Lightbulb,
  GraduationCap,
  BarChart3,
  ArrowRight,
  Target,
  Trophy,
  Eye
} from 'lucide-react';
import { Button } from './ui/button';
import ReactMarkdown from 'react-markdown';

const BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const IGoAssist = ({ 
  isOpen, 
  onClose, 
  initialPrompt = '', 
  currentPage = '',
  onOptimizedPrompt = null 
}) => {
  const [activeTab, setActiveTab] = useState('optimize');
  const [inputText, setInputText] = useState(initialPrompt);
  const [response, setResponse] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [detectedType, setDetectedType] = useState(null);
  const [copied, setCopied] = useState(false);
  const [videoTypes, setVideoTypes] = useState({});
  const [selectedExampleType, setSelectedExampleType] = useState('multi_dialogue');
  const [examples, setExamples] = useState([]);
  const [pageContext, setPageContext] = useState(null);
  const [featureGuides, setFeatureGuides] = useState([]);
  const [selectedGuide, setSelectedGuide] = useState(null);
  const [promptAnalysis, setPromptAnalysis] = useState(null);
  const [showBeforeAfter, setShowBeforeAfter] = useState(false);
  const [originalPrompt, setOriginalPrompt] = useState('');
  const [practiceResult, setPracticeResult] = useState(null);
  const [showPerfectExample, setShowPerfectExample] = useState(false);
  const responseRef = useRef(null);

  useEffect(() => {
    if (initialPrompt) {
      setInputText(initialPrompt);
    }
  }, [initialPrompt]);

  useEffect(() => {
    fetch(`${BASE_URL}/api/igoassist/video-types`)
      .then(res => res.json())
      .then(data => setVideoTypes(data.video_types || {}))
      .catch(err => console.error('Failed to fetch video types:', err));
    
    fetch(`${BASE_URL}/api/igoassist/feature-guides`)
      .then(res => res.json())
      .then(data => setFeatureGuides(data.guides || []))
      .catch(err => console.error('Failed to fetch feature guides:', err));
  }, []);

  useEffect(() => {
    if (currentPage) {
      fetch(`${BASE_URL}/api/igoassist/page-context/${encodeURIComponent(currentPage)}`)
        .then(res => res.json())
        .then(data => setPageContext(data.context))
        .catch(err => console.error('Failed to fetch page context:', err));
    }
  }, [currentPage]);

  useEffect(() => {
    if (responseRef.current) {
      responseRef.current.scrollTop = responseRef.current.scrollHeight;
    }
  }, [response]);

  const detectVideoType = async (prompt) => {
    try {
      const res = await fetch(`${BASE_URL}/api/igoassist/detect-type`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt })
      });
      const data = await res.json();
      setDetectedType(data);
      return data;
    } catch (err) {
      console.error('Detection error:', err);
      return null;
    }
  };

  const analyzePrompt = async (prompt) => {
    try {
      const res = await fetch(`${BASE_URL}/api/igoassist/analyze-prompt`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt })
      });
      
      if (!res.ok) {
        console.error('Analysis API error:', res.status);
        return null;
      }
      
      const data = await res.json();
      setPromptAnalysis(data.analysis);
      setDetectedType(data.detected_type);
      return data;
    } catch (err) {
      console.error('Analysis error:', err);
      return null;
    }
  };

  const handleOptimize = async () => {
    if (!inputText.trim() || isLoading) return;
    
    setIsLoading(true);
    setResponse('');
    setOriginalPrompt(inputText);
    setShowBeforeAfter(false);
    
    await analyzePrompt(inputText);
    
    try {
      const res = await fetch(`${BASE_URL}/api/igoassist/optimize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: inputText })
      });
      
      if (!res.ok) {
        setResponse(`Error: Server error (${res.status}). Please try again.`);
        return;
      }
      
      const data = await res.json();
      if (data.response) {
        setResponse(data.response);
        setShowBeforeAfter(true);
      } else if (data.error) {
        setResponse(`Error: ${data.error}`);
      }
    } catch (err) {
      setResponse('Error: Failed to optimize prompt. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAsk = async () => {
    if (!inputText.trim() || isLoading) return;
    
    setIsLoading(true);
    setResponse('');
    
    try {
      const res = await fetch(`${BASE_URL}/api/igoassist/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question: inputText, current_page: currentPage })
      });
      
      if (!res.ok) {
        setResponse(`Error: Server error (${res.status}). Please try again.`);
        return;
      }
      
      const data = await res.json();
      if (data.response) {
        setResponse(data.response);
      } else if (data.error) {
        setResponse(`Error: ${data.error}`);
      }
    } catch (err) {
      setResponse('Error: Failed to get answer. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = () => {
    if (activeTab === 'optimize') {
      handleOptimize();
    } else if (activeTab === 'ask') {
      handleAsk();
    } else if (activeTab === 'practice') {
      handlePractice();
    }
  };

  const handlePractice = async () => {
    if (!inputText.trim() || isLoading) return;
    
    setIsLoading(true);
    setPracticeResult(null);
    setShowPerfectExample(false);
    
    try {
      const res = await fetch(`${BASE_URL}/api/igoassist/practice`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: inputText })
      });
      
      if (!res.ok) {
        setPracticeResult({ error: `Server error (${res.status}). Please try again.` });
        return;
      }
      
      const data = await res.json();
      setPracticeResult(data);
    } catch (err) {
      console.error('Practice error:', err);
      setPracticeResult({ error: 'Failed to analyze prompt. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchExamples = async (type) => {
    try {
      const res = await fetch(`${BASE_URL}/api/igoassist/examples/${type}`);
      const data = await res.json();
      setExamples(data.examples || []);
    } catch (err) {
      console.error('Failed to fetch examples:', err);
    }
  };

  useEffect(() => {
    if (activeTab === 'examples') {
      fetchExamples(selectedExampleType);
    }
  }, [activeTab, selectedExampleType]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Parse structured response into sections
  const parseOptimizeResponse = (text) => {
    if (!text) return null;
    
    const sections = {
      explanation: '',
      prompt: '',
      tips: ''
    };
    
    // Try to parse structured format first
    const explanationMatch = text.match(/---EXPLANATION---\s*([\s\S]*?)(?=---PROMPT---|$)/i);
    const promptMatch = text.match(/---PROMPT---\s*([\s\S]*?)(?=---TIPS---|$)/i);
    const tipsMatch = text.match(/---TIPS---\s*([\s\S]*?)$/i);
    
    if (promptMatch) {
      sections.explanation = explanationMatch ? explanationMatch[1].trim() : '';
      sections.prompt = promptMatch[1].trim();
      sections.tips = tipsMatch ? tipsMatch[1].trim() : '';
      return sections;
    }
    
    // Fallback: try to extract code block
    const codeMatch = text.match(/```[\s\S]*?\n([\s\S]*?)```/);
    if (codeMatch) {
      const beforeCode = text.substring(0, text.indexOf('```')).trim();
      const afterCode = text.substring(text.lastIndexOf('```') + 3).trim();
      sections.explanation = beforeCode;
      sections.prompt = codeMatch[1].trim();
      sections.tips = afterCode;
      return sections;
    }
    
    // No structure found, return full text as prompt
    return null;
  };

  const extractCodeBlock = (text) => {
    const parsed = parseOptimizeResponse(text);
    return parsed ? parsed.prompt : null;
  };

  const useOptimizedPrompt = () => {
    const parsed = parseOptimizeResponse(response);
    const promptText = parsed ? parsed.prompt : null;
    if (promptText && onOptimizedPrompt) {
      onOptimizedPrompt(promptText);
      onClose();
    }
  };

  const loadFeatureGuide = async (guideId) => {
    try {
      const res = await fetch(`${BASE_URL}/api/igoassist/feature-guide/${guideId}`);
      const data = await res.json();
      setSelectedGuide(data.guide);
    } catch (err) {
      console.error('Failed to load guide:', err);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-[#1a1a2e] border border-[#00F0FF]/30 rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden shadow-2xl"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#00F0FF] to-[#A855F7] flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">iGoAssist</h2>
                <p className="text-xs text-white/60">Your AI video creation helper</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              data-testid="igoassist-close"
            >
              <X className="w-5 h-5 text-white/70" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-white/10 overflow-x-auto">
            {[
              { id: 'optimize', icon: Wand2, label: 'Optimize' },
              { id: 'learn', icon: GraduationCap, label: 'Learn' },
              { id: 'ask', icon: HelpCircle, label: 'Ask' },
              { id: 'examples', icon: BookOpen, label: 'Examples' },
              { id: 'practice', icon: Target, label: 'Practice' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => { setActiveTab(tab.id); setSelectedGuide(null); setPracticeResult(null); }}
                data-testid={`igoassist-tab-${tab.id}`}
                className={`flex-1 py-3 px-2 text-sm font-medium transition-colors flex items-center justify-center gap-1.5 min-w-[60px] ${
                  activeTab === tab.id 
                    ? 'text-[#00F0FF] border-b-2 border-[#00F0FF] bg-[#00F0FF]/5' 
                    : 'text-white/60 hover:text-white/80'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="p-4 space-y-4 max-h-[calc(85vh-180px)] overflow-y-auto">
            
            {/* PRACTICE TAB - Learning Mode */}
            {activeTab === 'practice' && (
              <div className="space-y-4">
                <div className="bg-gradient-to-r from-[#A855F7]/10 to-[#00F0FF]/10 rounded-lg p-4 border border-[#A855F7]/20">
                  <div className="flex items-center gap-2 mb-2">
                    <Target className="w-5 h-5 text-[#A855F7]" />
                    <span className="font-medium text-white">Practice Mode</span>
                  </div>
                  <p className="text-sm text-white/70">
                    Write a prompt and get instant feedback on your prompting skills. See what elements you&apos;re missing and how to improve.
                  </p>
                </div>
                
                {/* Practice Input */}
                <div className="space-y-2">
                  <label className="text-sm text-white/70">Write your practice prompt:</label>
                  <textarea
                    value={inputText}
                    onChange={(e) => setInputText(e.target.value)}
                    placeholder="Try writing a prompt for a video scene... Include SCENE:, character descriptions, dialogue with emotions, and camera directions."
                    className="w-full bg-[#0f0f1a] border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/30 min-h-[120px] resize-none focus:outline-none focus:border-[#A855F7]/50"
                    data-testid="practice-input"
                  />
                </div>
                
                <Button
                  onClick={handlePractice}
                  disabled={!inputText.trim() || isLoading}
                  className="w-full bg-gradient-to-r from-[#A855F7] to-[#00F0FF] hover:opacity-90 text-white font-medium"
                  data-testid="practice-submit"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <Target className="w-4 h-4 mr-2" />
                      Score My Prompt
                    </>
                  )}
                </Button>
                
                {/* Practice Results */}
                {practiceResult && (
                  <div className="space-y-4">
                    {/* Error Display */}
                    {practiceResult.error ? (
                      <div className="bg-red-500/10 rounded-lg p-4 border border-red-500/30">
                        <p className="text-red-400 text-sm">{practiceResult.error}</p>
                      </div>
                    ) : (
                    <>
                    {/* Score Display */}
                    <div className="bg-[#0f0f1a] rounded-lg p-4 border border-white/10">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <Trophy className={`w-8 h-8 ${
                            practiceResult.practice_score >= 80 ? 'text-yellow-400' :
                            practiceResult.practice_score >= 60 ? 'text-blue-400' :
                            practiceResult.practice_score >= 40 ? 'text-orange-400' : 'text-gray-400'
                          }`} />
                          <div>
                            <div className="text-2xl font-bold text-white">{practiceResult.practice_score}/100</div>
                            <div className={`text-sm font-medium ${
                              practiceResult.skill_level === 'Expert' ? 'text-yellow-400' :
                              practiceResult.skill_level === 'Advanced' ? 'text-green-400' :
                              practiceResult.skill_level === 'Intermediate' ? 'text-blue-400' :
                              practiceResult.skill_level === 'Beginner' ? 'text-orange-400' : 'text-gray-400'
                            }`}>{practiceResult.skill_level}</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-xs text-white/50">Video Type</div>
                          <div className="text-sm text-[#00F0FF]">{practiceResult.video_type}</div>
                        </div>
                      </div>
                      
                      {/* Score Bar */}
                      <div className="h-3 bg-white/10 rounded-full overflow-hidden mb-2">
                        <div 
                          className={`h-full transition-all duration-1000 ${
                            practiceResult.practice_score >= 80 ? 'bg-gradient-to-r from-yellow-500 to-yellow-300' :
                            practiceResult.practice_score >= 60 ? 'bg-gradient-to-r from-green-500 to-green-300' :
                            practiceResult.practice_score >= 40 ? 'bg-gradient-to-r from-blue-500 to-blue-300' : 'bg-gradient-to-r from-orange-500 to-orange-300'
                          }`}
                          style={{ width: `${practiceResult.practice_score}%` }}
                        />
                      </div>
                      <p className="text-sm text-white/60">{practiceResult.skill_message}</p>
                    </div>
                    
                    {/* Elements Breakdown */}
                    <div className="bg-[#0f0f1a] rounded-lg p-4 border border-white/10">
                      <h4 className="font-medium text-white mb-3">Elements Breakdown</h4>
                      <div className="space-y-2">
                        {practiceResult.elements_breakdown?.map((elem, i) => (
                          <div key={i} className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className={elem.present ? 'text-green-400' : 'text-red-400'}>
                                {elem.present ? '✓' : '✗'}
                              </span>
                              <span className="text-sm text-white/80">{elem.element}</span>
                            </div>
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              elem.present ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                            }`}>
                              {elem.earned}/{elem.weight}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Improvement Tips */}
                    {practiceResult.improvement_tips?.length > 0 && (
                      <div className="bg-[#A855F7]/10 rounded-lg p-4 border border-[#A855F7]/20">
                        <h4 className="font-medium text-[#A855F7] mb-2">💡 Focus on These</h4>
                        <ul className="space-y-2">
                          {practiceResult.improvement_tips.map((tip, i) => (
                            <li key={i} className="text-sm text-white/70 flex items-start gap-2">
                              <span className="text-[#A855F7]">•</span>
                              {tip}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {/* Perfect Example Toggle */}
                    <Button
                      onClick={() => setShowPerfectExample(!showPerfectExample)}
                      variant="outline"
                      className="w-full border-[#00F0FF]/30 text-[#00F0FF] hover:bg-[#00F0FF]/10"
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      {showPerfectExample ? 'Hide' : 'Show'} Perfect Example
                    </Button>
                    
                    {/* Perfect Example */}
                    {showPerfectExample && practiceResult.perfect_example && (
                      <div className="bg-[#0f0f1a] rounded-lg p-4 border-2 border-[#00F0FF]/30">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-medium text-[#00F0FF]">✨ {practiceResult.perfect_example.title}</h4>
                          <button
                            onClick={() => copyToClipboard(practiceResult.perfect_example.prompt)}
                            className="flex items-center gap-1 px-2 py-1 bg-[#00F0FF]/10 hover:bg-[#00F0FF]/20 rounded text-xs text-[#00F0FF]"
                          >
                            {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                            {copied ? 'Copied!' : 'Copy'}
                          </button>
                        </div>
                        <pre className="text-sm text-white/80 whitespace-pre-wrap font-mono bg-black/30 p-3 rounded max-h-[200px] overflow-y-auto">
                          {practiceResult.perfect_example.prompt}
                        </pre>
                        <div className="mt-3 flex flex-wrap gap-1">
                          {practiceResult.perfect_example.elements?.map((elem, i) => (
                            <span key={i} className="text-xs bg-[#00F0FF]/10 text-[#00F0FF] px-2 py-0.5 rounded">
                              {elem}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                  )}
                  </div>
                )}
              </div>
            )}
            
            {/* LEARN TAB - Phase 2: Feature Guides */}
            {activeTab === 'learn' && (
              <div className="space-y-4">
                {/* Page Context Tips */}
                {pageContext && !selectedGuide && (
                  <div className="bg-gradient-to-r from-[#00F0FF]/10 to-[#A855F7]/10 rounded-lg p-4 border border-[#00F0FF]/20">
                    <div className="flex items-center gap-2 mb-2">
                      <Lightbulb className="w-5 h-5 text-[#00F0FF]" />
                      <span className="font-medium text-white">Tips for {pageContext.name}</span>
                    </div>
                    <ul className="space-y-2">
                      {pageContext.tips?.map((tip, i) => (
                        <li key={i} className="text-sm text-white/70 flex items-start gap-2">
                          <ChevronRight className="w-4 h-4 text-[#00F0FF] mt-0.5 flex-shrink-0" />
                          {tip}
                        </li>
                      ))}
                    </ul>
                    {pageContext.common_questions?.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-white/10">
                        <p className="text-xs text-white/50 mb-2">Quick questions:</p>
                        <div className="flex flex-wrap gap-2">
                          {pageContext.common_questions.map((q, i) => (
                            <button
                              key={i}
                              onClick={() => { setInputText(q); setActiveTab('ask'); }}
                              className="text-xs bg-white/5 hover:bg-white/10 text-white/70 px-2 py-1 rounded-full transition-colors"
                            >
                              {q}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Feature Guide Detail */}
                {selectedGuide ? (
                  <div className="space-y-4">
                    <button 
                      onClick={() => setSelectedGuide(null)}
                      className="text-sm text-[#00F0FF] hover:underline flex items-center gap-1"
                    >
                      ← Back to all guides
                    </button>
                    <div className="bg-[#0f0f1a] rounded-lg p-4 border border-white/10">
                      <h3 className="text-lg font-semibold text-white mb-2">{selectedGuide.title}</h3>
                      <p className="text-sm text-white/60 mb-4">{selectedGuide.description}</p>
                      
                      <div className="space-y-2 mb-4">
                        {selectedGuide.steps?.map((step, i) => (
                          <div key={i} className={`text-sm ${step.startsWith('**') ? 'font-semibold text-[#00F0FF] mt-3' : 'text-white/80 pl-2'}`}>
                            {step.replace(/\*\*/g, '')}
                          </div>
                        ))}
                      </div>
                      
                      {selectedGuide.tips?.length > 0 && (
                        <div className="bg-[#00F0FF]/10 rounded-lg p-3 mt-4">
                          <p className="text-xs font-medium text-[#00F0FF] mb-2">💡 Pro Tips</p>
                          {selectedGuide.tips.map((tip, i) => (
                            <p key={i} className="text-sm text-white/70">{tip}</p>
                          ))}
                        </div>
                      )}
                      
                      {/* Example Prompt - Copyable */}
                      {selectedGuide.example_prompt && (
                        <div className="mt-4 bg-[#A855F7]/10 rounded-lg p-4 border border-[#A855F7]/20">
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-xs font-medium text-[#A855F7]">📝 Try This Example</p>
                            <div className="flex gap-2">
                              <button
                                onClick={() => copyToClipboard(selectedGuide.example_prompt)}
                                className="flex items-center gap-1 px-2 py-1 bg-[#A855F7]/20 hover:bg-[#A855F7]/30 rounded text-xs text-[#A855F7]"
                              >
                                {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                                {copied ? 'Copied!' : 'Copy'}
                              </button>
                              <button
                                onClick={() => { setInputText(selectedGuide.example_prompt); setActiveTab('practice'); }}
                                className="flex items-center gap-1 px-2 py-1 bg-[#00F0FF]/20 hover:bg-[#00F0FF]/30 rounded text-xs text-[#00F0FF]"
                              >
                                <Target className="w-3 h-3" />
                                Practice
                              </button>
                            </div>
                          </div>
                          <pre className="text-sm text-white/80 whitespace-pre-wrap font-mono bg-black/30 p-3 rounded max-h-[150px] overflow-y-auto">
                            {selectedGuide.example_prompt}
                          </pre>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  /* Feature Guides List */
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium text-white/70">Step-by-Step Guides</h4>
                    <div className="grid gap-2">
                      {featureGuides.map(guide => (
                        <button
                          key={guide.id}
                          onClick={() => loadFeatureGuide(guide.id)}
                          className="w-full text-left bg-[#0f0f1a] hover:bg-[#0f0f1a]/80 rounded-lg p-3 border border-white/10 hover:border-[#00F0FF]/30 transition-all group"
                          data-testid={`guide-${guide.id}`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <h5 className="font-medium text-white group-hover:text-[#00F0FF] transition-colors">{guide.title}</h5>
                              <p className="text-xs text-white/50 mt-1">{guide.description}</p>
                            </div>
                            <ArrowRight className="w-4 h-4 text-white/30 group-hover:text-[#00F0FF] transition-colors" />
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* EXAMPLES TAB */}
            {activeTab === 'examples' && (
              <div className="space-y-4">
                <div className="relative">
                  <select
                    value={selectedExampleType}
                    onChange={(e) => setSelectedExampleType(e.target.value)}
                    className="w-full bg-[#0f0f1a] border border-white/10 rounded-lg px-4 py-2 text-white appearance-none cursor-pointer"
                    data-testid="example-type-selector"
                  >
                    {Object.entries(videoTypes).map(([key, info]) => (
                      <option key={key} value={key}>{info.name}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/50 pointer-events-none" />
                </div>

                {videoTypes[selectedExampleType] && (
                  <div className="bg-[#0f0f1a] rounded-lg p-3 border border-white/10">
                    <p className="text-sm text-white/70 mb-2">{videoTypes[selectedExampleType].description}</p>
                    <div className="flex flex-wrap gap-2">
                      {videoTypes[selectedExampleType].tips?.map((tip, i) => (
                        <span key={i} className="text-xs bg-[#00F0FF]/10 text-[#00F0FF] px-2 py-1 rounded">
                          {tip}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {examples.map((example, i) => (
                  <div key={i} className="bg-[#0f0f1a] rounded-lg p-4 border border-white/10">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-white">{example.title}</h4>
                      <div className="flex gap-2">
                        <button
                          onClick={() => copyToClipboard(example.prompt)}
                          className="p-1.5 hover:bg-white/10 rounded transition-colors"
                          title="Copy prompt"
                        >
                          {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4 text-white/50" />}
                        </button>
                        <button
                          onClick={() => { setInputText(example.prompt); setActiveTab('optimize'); }}
                          className="text-xs bg-[#00F0FF]/20 hover:bg-[#00F0FF]/30 text-[#00F0FF] px-2 py-1 rounded transition-colors"
                        >
                          Use this
                        </button>
                      </div>
                    </div>
                    <pre className="text-sm text-white/70 whitespace-pre-wrap font-mono bg-black/30 p-3 rounded max-h-[150px] overflow-y-auto">
                      {example.prompt}
                    </pre>
                  </div>
                ))}
              </div>
            )}

            {/* OPTIMIZE & ASK TABS */}
            {(activeTab === 'optimize' || activeTab === 'ask') && (
              <>
                {/* Prompt Analysis - Phase 3 */}
                {promptAnalysis && activeTab === 'optimize' && (
                  <div className="bg-[#0f0f1a] rounded-lg p-4 border border-white/10">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <BarChart3 className="w-5 h-5 text-[#00F0FF]" />
                        <span className="font-medium text-white">Prompt Score</span>
                      </div>
                      <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                        promptAnalysis.rating_color === 'green' ? 'bg-green-500/20 text-green-400' :
                        promptAnalysis.rating_color === 'blue' ? 'bg-blue-500/20 text-blue-400' :
                        promptAnalysis.rating_color === 'yellow' ? 'bg-yellow-500/20 text-yellow-400' :
                        'bg-red-500/20 text-red-400'
                      }`}>
                        {promptAnalysis.percentage}% - {promptAnalysis.rating}
                      </div>
                    </div>
                    
                    {/* Score Bar */}
                    <div className="h-2 bg-white/10 rounded-full mb-3 overflow-hidden">
                      <div 
                        className={`h-full transition-all duration-500 ${
                          promptAnalysis.percentage >= 80 ? 'bg-green-500' :
                          promptAnalysis.percentage >= 60 ? 'bg-blue-500' :
                          promptAnalysis.percentage >= 40 ? 'bg-yellow-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${promptAnalysis.percentage}%` }}
                      />
                    </div>
                    
                    {/* Checks */}
                    <div className="space-y-1.5">
                      {promptAnalysis.checks?.map((check, i) => (
                        <div key={i} className={`text-xs flex items-center gap-2 ${check.passed ? 'text-white/70' : 'text-yellow-400/80'}`}>
                          <span>{check.message}</span>
                        </div>
                      ))}
                    </div>
                    
                    {/* Suggestions */}
                    {promptAnalysis.suggestions?.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-white/10">
                        <p className="text-xs text-white/50 mb-2">Suggestions to improve:</p>
                        {promptAnalysis.suggestions.map((sug, i) => (
                          <p key={i} className="text-xs text-[#00F0FF]/80 mb-1">• {sug}</p>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Detected Type Badge */}
                {detectedType && activeTab === 'optimize' && !promptAnalysis && (
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-white/60">Detected:</span>
                    <span className="px-2 py-1 bg-[#A855F7]/20 text-[#A855F7] rounded-full text-xs font-medium">
                      {detectedType.type_info?.name}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      detectedType.confidence === 'high' ? 'bg-green-500/20 text-green-400' :
                      detectedType.confidence === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                      'bg-red-500/20 text-red-400'
                    }`}>
                      {detectedType.confidence} confidence
                    </span>
                  </div>
                )}

                {/* Input */}
                <div className="space-y-2">
                  <label className="text-sm text-white/70">
                    {activeTab === 'optimize' ? 'Your video idea or prompt:' : 'Your question:'}
                  </label>
                  <textarea
                    value={inputText}
                    onChange={(e) => setInputText(e.target.value)}
                    placeholder={activeTab === 'optimize' 
                      ? "Describe your video idea... e.g., 'A couple talking on a park bench'"
                      : "Ask anything about iGoViral... e.g., 'How do I create a dialogue video?'"
                    }
                    className="w-full bg-[#0f0f1a] border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/30 min-h-[100px] resize-none focus:outline-none focus:border-[#00F0FF]/50"
                    data-testid="igoassist-input"
                  />
                </div>

                {/* Submit Button */}
                <Button
                  onClick={handleSubmit}
                  disabled={!inputText.trim() || isLoading}
                  className="w-full bg-gradient-to-r from-[#00F0FF] to-[#A855F7] hover:opacity-90 text-white font-medium"
                  data-testid="igoassist-submit"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {activeTab === 'optimize' ? 'Analyzing & Optimizing...' : 'Thinking...'}
                    </>
                  ) : (
                    <>
                      {activeTab === 'optimize' ? <Wand2 className="w-4 h-4 mr-2" /> : <HelpCircle className="w-4 h-4 mr-2" />}
                      {activeTab === 'optimize' ? 'Optimize My Prompt' : 'Ask iGoAssist'}
                    </>
                  )}
                </Button>

                {/* Before/After Preview - Phase 3 */}
                {showBeforeAfter && originalPrompt && response && activeTab === 'optimize' && (
                  <div className="bg-[#0f0f1a] rounded-lg border border-white/10 overflow-hidden">
                    <div className="grid grid-cols-2 divide-x divide-white/10">
                      <div className="p-3">
                        <p className="text-xs font-medium text-red-400 mb-2">❌ Before</p>
                        <p className="text-xs text-white/60 line-clamp-4">{originalPrompt}</p>
                      </div>
                      <div className="p-3">
                        <p className="text-xs font-medium text-green-400 mb-2">✅ After</p>
                        <p className="text-xs text-white/60 line-clamp-4">{extractCodeBlock(response) || 'See optimized prompt below'}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Response */}
                {response && (
                  <div className="space-y-3">
                    {activeTab === 'optimize' && parseOptimizeResponse(response) ? (
                      // Structured optimize response
                      <>
                        {/* Explanation Section */}
                        {parseOptimizeResponse(response).explanation && (
                          <div className="bg-[#0f0f1a] border border-white/10 rounded-lg p-4">
                            <p className="text-xs font-medium text-white/50 mb-2">What was improved:</p>
                            <div className="text-sm text-white/70 prose prose-invert prose-sm max-w-none">
                              <ReactMarkdown>{parseOptimizeResponse(response).explanation}</ReactMarkdown>
                            </div>
                          </div>
                        )}
                        
                        {/* Optimized Prompt Section - Copyable */}
                        <div className="bg-[#0f0f1a] border-2 border-[#00F0FF]/30 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-xs font-medium text-[#00F0FF]">✨ Optimized Prompt (Ready to use)</p>
                            <button
                              onClick={() => copyToClipboard(parseOptimizeResponse(response).prompt)}
                              className="flex items-center gap-1 px-2 py-1 bg-[#00F0FF]/10 hover:bg-[#00F0FF]/20 rounded text-xs text-[#00F0FF] transition-colors"
                              title="Copy prompt"
                              data-testid="copy-optimized-prompt"
                            >
                              {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                              {copied ? 'Copied!' : 'Copy'}
                            </button>
                          </div>
                          <pre className="text-sm text-white whitespace-pre-wrap font-mono bg-black/30 p-3 rounded max-h-[200px] overflow-y-auto">
                            {parseOptimizeResponse(response).prompt}
                          </pre>
                        </div>
                        
                        {/* Tips Section */}
                        {parseOptimizeResponse(response).tips && (
                          <div className="bg-[#A855F7]/10 border border-[#A855F7]/20 rounded-lg p-3">
                            <p className="text-xs font-medium text-[#A855F7] mb-2">💡 Tips</p>
                            <div className="text-sm text-white/70 prose prose-invert prose-sm max-w-none">
                              <ReactMarkdown>{parseOptimizeResponse(response).tips}</ReactMarkdown>
                            </div>
                          </div>
                        )}
                        
                        {onOptimizedPrompt && (
                          <Button
                            onClick={useOptimizedPrompt}
                            className="w-full bg-[#00F0FF]/20 hover:bg-[#00F0FF]/30 text-[#00F0FF] border border-[#00F0FF]/30"
                            data-testid="use-optimized-prompt"
                          >
                            <Check className="w-4 h-4 mr-2" />
                            Use This Optimized Prompt
                          </Button>
                        )}
                      </>
                    ) : (
                      // Unstructured response (Ask tab or fallback)
                      <>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-white/70">Response:</span>
                          <button
                            onClick={() => copyToClipboard(response)}
                            className="p-1.5 hover:bg-white/10 rounded transition-colors"
                            title="Copy response"
                          >
                            {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4 text-white/50" />}
                          </button>
                        </div>
                        <div 
                          ref={responseRef}
                          className="bg-[#0f0f1a] border border-white/10 rounded-lg p-4 max-h-[250px] overflow-y-auto prose prose-invert prose-sm max-w-none"
                        >
                          <ReactMarkdown>{response}</ReactMarkdown>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Floating trigger button component
export const IGoAssistTrigger = ({ onClick, className = '' }) => {
  return (
    <motion.button
      onClick={onClick}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      className={`fixed bottom-6 right-6 w-14 h-14 rounded-full bg-gradient-to-br from-[#00F0FF] to-[#A855F7] shadow-lg shadow-[#00F0FF]/25 flex items-center justify-center z-40 ${className}`}
      data-testid="igoassist-trigger"
    >
      <MessageCircle className="w-6 h-6 text-white" />
    </motion.button>
  );
};

// Inline help button for specific features
export const IGoAssistInlineHelp = ({ feature, onClick, className = '' }) => {
  return (
    <button
      onClick={() => onClick(feature)}
      className={`p-1 hover:bg-white/10 rounded-full transition-colors ${className}`}
      title={`Get help with ${feature}`}
    >
      <HelpCircle className="w-4 h-4 text-white/50 hover:text-[#00F0FF]" />
    </button>
  );
};

export default IGoAssist;
